/**
 * Token自动刷新服务
 * 在token过期前自动刷新，保持用户登录状态
 */

import { getToken, getRefreshToken, setToken, removeToken } from './auth';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// 刷新间隔：每20小时刷新一次（token有效期1天）
const REFRESH_INTERVAL = 20 * 60 * 60 * 1000; // 20 hours in milliseconds

let refreshTimer: NodeJS.Timeout | null = null;

/**
 * 刷新access token
 */
const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    console.log('No refresh token available');
    return false;
  }

  try {
    const response = await axios.post(`${API_URL}/auth/refresh`, {
      refresh_token: refreshToken
    });

    const { access_token } = response.data;
    setToken(access_token);
    console.log('Token refreshed successfully');
    return true;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return false;
  }
};

/**
 * 启动token自动刷新
 */
export const startTokenRefresh = () => {
  // 清除现有的定时器
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }

  // 检查是否有token
  const token = getToken();
  if (!token) {
    console.log('No token, skipping auto refresh');
    return;
  }

  console.log('Starting token auto-refresh service');

  // 立即刷新一次（如果token快过期了）
  refreshAccessToken();

  // 设置定时刷新
  refreshTimer = setInterval(async () => {
    const currentToken = getToken();
    if (currentToken) {
      const success = await refreshAccessToken();
      if (!success) {
        // 刷新失败，停止定时器
        stopTokenRefresh();
      }
    } else {
      // 没有token，停止定时器
      stopTokenRefresh();
    }
  }, REFRESH_INTERVAL);
};

/**
 * 停止token自动刷新
 */
export const stopTokenRefresh = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
    console.log('Stopped token auto-refresh service');
  }
};

/**
 * 在用户活动时刷新token
 */
export const refreshOnActivity = () => {
  let lastActivityTime = Date.now();
  let activityTimer: NodeJS.Timeout | null = null;

  const handleActivity = () => {
    lastActivityTime = Date.now();
  };

  // 监听用户活动
  window.addEventListener('mousemove', handleActivity);
  window.addEventListener('keydown', handleActivity);
  window.addEventListener('click', handleActivity);
  window.addEventListener('scroll', handleActivity);

  // 每小时检查一次，如果用户有活动且token快过期，则刷新
  activityTimer = setInterval(async () => {
    const timeSinceActivity = Date.now() - lastActivityTime;
    
    // 如果用户在过去1小时内有活动
    if (timeSinceActivity < 60 * 60 * 1000) {
      const token = getToken();
      if (token) {
        await refreshAccessToken();
      }
    }
  }, 60 * 60 * 1000); // 每小时检查一次

  // 返回清理函数
  return () => {
    window.removeEventListener('mousemove', handleActivity);
    window.removeEventListener('keydown', handleActivity);
    window.removeEventListener('click', handleActivity);
    window.removeEventListener('scroll', handleActivity);
    if (activityTimer) {
      clearInterval(activityTimer);
    }
  };
};
